import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * How often the frontend sends a heartbeat while the app tab is visible.
 * Exported so backend and frontend stay in sync.
 */
export const USER_SESSION_HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * A heartbeat arriving more than this long after the previous one starts a
 * new session instead of extending the current one. Generous multiple of the
 * heartbeat interval so a few dropped beats don't split a session.
 */
export const USER_SESSION_IDLE_GAP_MS = 5 * 60_000;

/**
 * The frontend only beats while the person has interacted (pointer, keyboard,
 * scroll, touch, tab focus) within this window. A visible tab alone is not
 * presence: a tab left open on a second monitor or behind a locked screen
 * stays `visible`, and beating on visibility alone stitched idle nights into
 * 15–50 hour sessions.
 */
export const USER_SESSION_INPUT_IDLE_MS = 5 * 60_000;

export const UserSessionHeartbeatOutputSchema = z.object({
  sessionId: z.uuid(),
  startedAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
});

export const UserSessionSummaryInputSchema = z.object({
  /** Reporting window: sessions active within the last N days. */
  days: z.number().int().min(1).max(90).default(30),
});

export const UserSessionUserSummarySchema = z.object({
  userId: z.uuid(),
  userName: z.string(),
  sessionCount: z.number().int(),
  totalDurationMs: z.number(),
  avgSessionDurationMs: z.number(),
  lastSeenAt: z.iso.datetime(),
});

export const UserSessionSummaryOutputSchema = z.object({
  days: z.number().int(),
  activeUserCount: z.number().int(),
  totalSessionCount: z.number().int(),
  totalDurationMs: z.number(),
  avgSessionDurationMs: z.number(),
  users: z.array(UserSessionUserSummarySchema),
});

export type UserSessionHeartbeatOutput = z.infer<
  typeof UserSessionHeartbeatOutputSchema
>;
export type UserSessionSummaryInput = z.infer<
  typeof UserSessionSummaryInputSchema
>;
export type UserSessionUserSummary = z.infer<
  typeof UserSessionUserSummarySchema
>;
export type UserSessionSummaryOutput = z.infer<
  typeof UserSessionSummaryOutputSchema
>;

export const userSessionsContracts = {
  /**
   * Activity ping from the frontend. Extends the caller's current session or
   * starts a new one when the idle gap has passed.
   */
  heartbeat: oc.output(UserSessionHeartbeatOutputSchema).route({
    method: "POST",
    summary: "Record app activity heartbeat",
    description:
      "Pings that the current user is active in the app, extending their current session or starting a new one after five idle minutes. Returns the session id and timestamps.",
  }),

  /** Admin-only per-user time-in-app report for the caller's organization. */
  summary: oc
    .input(UserSessionSummaryInputSchema)
    .output(UserSessionSummaryOutputSchema)
    .route({
      method: "GET",
      summary: "Report time spent in the app per user",
      description:
        "Admin-only report of user sessions in your organization over the last N days (default 30): active users, session counts, total and average durations.",
    }),
} as const;
